<script setup lang="ts">
import PrimeSelect from 'primevue/select';
import type { SelectChangeEvent, SelectFilterEvent, SelectProps } from 'primevue/select';
import { computed, inject, unref, useSlots } from 'vue';
import { selectPlaceholderKey } from '../select-context.js';

defineOptions({ name: 'JvSelect', inheritAttrs: false });
const props = defineProps<{
  modelValue?: unknown;
  placeholder?: string | null | undefined;
  optionLabel?: SelectProps['optionLabel'];
  optionValue?: SelectProps['optionValue'];
  optionDisabled?: SelectProps['optionDisabled'];
  optionGroupLabel?: SelectProps['optionGroupLabel'];
  optionGroupChildren?: SelectProps['optionGroupChildren'];
}>();
const emit = defineEmits<{
  'update:modelValue': [value: SelectProps['modelValue']];
  change: [event: SelectChangeEvent];
  filter: [event: SelectFilterEvent];
}>();
const fallback = inject(selectPlaceholderKey, 'Select…');
const placeholder = computed(() => (props.placeholder?.trim() ? props.placeholder : unref(fallback)));
const slots = useSlots();
const forwardedSlots = computed(() => Object.keys(slots).filter((name) => name !== 'value'));
function hasValue(value: unknown) {
  return value !== null && value !== undefined && value !== '';
}
</script>

<template>
  <PrimeSelect
    v-bind="$attrs"
    :model-value="props.modelValue"
    :option-label="props.optionLabel"
    :option-value="props.optionValue"
    :option-disabled="props.optionDisabled"
    :option-group-label="props.optionGroupLabel"
    :option-group-children="props.optionGroupChildren"
    :placeholder="placeholder"
    class="jv-select"
    @update:model-value="emit('update:modelValue', $event)"
    @change="emit('change', $event)"
    @filter="emit('filter', $event)"
  >
    <template v-for="name in forwardedSlots" :key="name" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
    <template v-if="slots.value" #value="slotProps">
      <slot v-if="hasValue(slotProps.value)" name="value" v-bind="slotProps" />
      <span v-else class="jv-select-placeholder">{{ slotProps.placeholder }}</span>
    </template>
  </PrimeSelect>
</template>

<style scoped>
.jv-select {
  min-width: 0;
  min-height: 2.5rem;
}
.jv-select.p-select-sm {
  min-height: 2.125rem;
}
.jv-select.p-select-lg {
  min-height: 3rem;
}
.jv-select :deep(.p-select-label) {
  min-width: 0;
  line-height: 1.25rem;
}
.jv-select-placeholder {
  color: var(--jv-outline);
}
</style>
